import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useCommsGate } from '@/components/CommsUpgradeDialog';
import { formatRego, formatAUMobile } from '@/lib/formatters';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  MoreVertical,
  Calendar,
  Car,
  User,
  Wrench,
  Key,
  Eye,
  Trash2,
  RefreshCw,
  Send,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CalendarRange,
  LayoutGrid,
  List,
  X,
  Edit,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import BookingEditModal from '@/components/BookingEditModal';
import QuickBookingModal from '@/components/QuickBookingModal';
import RelatedItemsPanel from '@/components/RelatedItemsPanel';
import { formatMelbourne, formatMelbourneDate } from '@/lib/melbourneTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pickup: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  awaiting_parts: 'bg-orange-100 text-orange-700',
  ready: 'bg-green-100 text-green-700',
  completed: 'bg-slate-100 text-slate-700',
  cancelled: 'bg-red-100 text-red-500'
};

const SERVICE_TYPE_COLORS = {
  service: 'bg-blue-500',
  rwc: 'bg-green-500',
  tyres: 'bg-purple-500',
  aircon: 'bg-cyan-500',
  other: 'bg-gray-500'
};

const SERVICE_TYPE_BG_COLORS = {
  service: 'bg-blue-100 text-blue-700',
  rwc: 'bg-green-100 text-green-700',
  tyres: 'bg-purple-100 text-purple-700',
  aircon: 'bg-cyan-100 text-cyan-700',
  other: 'bg-gray-100 text-gray-700'
};

const SERVICE_TYPE_LABELS = {
  service: 'Service',
  rwc: 'RWC',
  tyres: 'Tyres',
  aircon: 'Aircon',
  other: 'Other'
};

// Weekly Calendar View Component
function WeeklyCalendarView({ weekData, onDayClick, selectedDate, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-6 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-lg"></div>
        ))}
      </div>
    );
  }

  // Filter out Sunday (only show Mon-Sat)
  const workDays = weekData?.stats?.slice(0, 6) || [];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="weekly-calendar-view">
      {workDays.map((day) => (
        <Card
          key={day.date}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
            day.is_current ? 'border-blue-500 bg-blue-50/50' : 
            selectedDate === day.date ? 'border-blue-400' : 'border-transparent'
          }`}
          onClick={() => onDayClick(day.date)}
          data-testid={`day-card-${day.date}`}
        >
          <div className="text-center mb-3">
            <p className={`text-sm font-medium ${day.is_current ? 'text-blue-600' : 'text-slate-500'}`}>
              {day.label}
            </p>
            <p className={`text-lg font-bold ${day.is_current ? 'text-blue-700' : 'text-slate-900'}`}>
              {day.display_date}
            </p>
          </div>
          
          <div className={`text-center py-3 rounded-lg mb-3 ${
            day.total > 0 ? 'bg-blue-100' : 'bg-slate-100'
          }`}>
            <p className={`text-3xl font-bold ${day.total > 0 ? 'text-blue-700' : 'text-slate-400'}`}>
              {day.total}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              booking{day.total !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Service Type Breakdown */}
          <div className="space-y-1">
            {Object.entries(day.by_service_type || {}).filter(([, count]) => count > 0).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${SERVICE_TYPE_COLORS[type]}`}></div>
                  <span className="text-slate-600">{SERVICE_TYPE_LABELS[type]}</span>
                </div>
                <span className="font-medium text-slate-700">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

// Monthly Calendar View Component
function MonthlyCalendarView({ monthData, onWeekClick, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-lg"></div>
        ))}
      </div>
    );
  }

  const weeks = monthData?.stats || [];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="monthly-calendar-view">
      {weeks.map((week, index) => (
        <Card
          key={index}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
            week.is_current ? 'border-blue-500 bg-blue-50/50' : 'border-transparent'
          }`}
          onClick={() => onWeekClick(week.week_start)}
          data-testid={`week-card-${index}`}
        >
          <div className="mb-3">
            <p className={`text-sm font-medium ${week.is_current ? 'text-blue-600' : 'text-slate-500'}`}>
              {week.label}
            </p>
            <p className="text-xs text-slate-400">
              {week.display_date}
            </p>
          </div>
          
          <div className={`text-center py-3 rounded-lg mb-3 ${
            week.total > 0 ? 'bg-blue-100' : 'bg-slate-100'
          }`}>
            <p className={`text-2xl font-bold ${week.total > 0 ? 'text-blue-700' : 'text-slate-400'}`}>
              {week.total}
            </p>
            <p className="text-xs text-slate-500">bookings</p>
          </div>

          <div className="grid grid-cols-5 gap-1">
            {Object.entries(week.by_service_type || {}).map(([type, count]) => (
              <div key={type} className="text-center" title={SERVICE_TYPE_LABELS[type]}>
                <div className={`w-full h-1.5 rounded-full ${count > 0 ? SERVICE_TYPE_COLORS[type] : 'bg-slate-200'}`}></div>
                <p className="text-[10px] mt-0.5 text-slate-500">{count}</p>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

// Yearly Calendar View Component
function YearlyCalendarView({ yearData, onMonthClick, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="h-28 bg-slate-100 animate-pulse rounded-lg"></div>
        ))}
      </div>
    );
  }

  const months = yearData?.stats || [];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="yearly-calendar-view">
      {months.map((month) => (
        <Card
          key={month.month}
          className={`p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
            month.is_current ? 'border-blue-500 bg-blue-50/50' : 'border-transparent'
          }`}
          onClick={() => onMonthClick(month.year, month.month)}
          data-testid={`month-card-${month.month}`}
        >
          <div className="text-center mb-2">
            <p className={`text-sm font-bold ${month.is_current ? 'text-blue-700' : 'text-slate-700'}`}>
              {month.label}
            </p>
          </div>
          
          <div className={`text-center py-2 rounded-lg ${
            month.total > 0 ? 'bg-blue-100' : 'bg-slate-100'
          }`}>
            <p className={`text-xl font-bold ${month.total > 0 ? 'text-blue-700' : 'text-slate-400'}`}>
              {month.total}
            </p>
            <p className="text-[10px] text-slate-500">bookings</p>
          </div>
        </Card>
      ))}
    </div>
  );
}

// Helper function to get service types display
function getServiceTypesDisplay(booking) {
  // Use service_types array if available, otherwise fall back to single service_type
  const types = booking.service_types && Array.isArray(booking.service_types) && booking.service_types.length > 0
    ? booking.service_types
    : (booking.service_type ? [booking.service_type] : []);
  
  return types;
}

// Day Detail Panel Component
function DayDetailPanel({ date, bookings, loading, onClose, onStatusUpdate, onDelete, onSendSMS, onEditBooking, onViewJobCard, user }) {
  if (!date) return null;

  const formattedDate = formatMelbourneDate(date + 'T12:00:00Z', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Card className="mt-6 p-6 border-2 border-blue-200 bg-blue-50/30" data-testid="day-detail-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{formattedDate}</h3>
          <p className="text-sm text-slate-500">{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No bookings for this day</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => {
            const serviceTypes = getServiceTypesDisplay(booking);
            const isCancelled = booking.status === 'cancelled';
            const hasLoanCar = booking.loan_car_requested === true;
            
            return (
              <Card 
                key={booking.booking_id} 
                className={`p-4 bg-white relative ${isCancelled ? 'opacity-60' : ''}`} 
                data-testid={`detail-booking-${booking.booking_id}`}
              >
                {/* Cross/Edit Icon - Top Right */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                  onClick={() => onEditBooking(booking)}
                  data-testid={`edit-booking-${booking.booking_id}`}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                
                <div className="flex items-start justify-between pr-10">
                  <div className="flex-1">
                    {/* Customer Name as Title - Clickable to Edit */}
                    <div 
                      className="font-semibold text-lg text-slate-900 mb-1 cursor-pointer hover:text-blue-600"
                      onClick={() => onEditBooking(booking)}
                    >
                      {booking.customer_name || 'Unknown Customer'}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {/* Vehicle Rego */}
                      <Badge variant="outline" className="font-mono">
                        {formatRego(booking.vehicle_rego) || '-'}
                      </Badge>
                      {/* Show all service types */}
                      {serviceTypes.map((type) => (
                        <Badge key={type} className={SERVICE_TYPE_BG_COLORS[type] || SERVICE_TYPE_BG_COLORS.other}>
                          {SERVICE_TYPE_LABELS[type] || type}
                        </Badge>
                      ))}
                      {/* Loan Car Badge - instead of "pickup" status */}
                      {hasLoanCar ? (
                        <Badge className="bg-cyan-100 text-cyan-700">
                          <Key className="h-3 w-3 mr-1" />
                          Loan Car
                        </Badge>
                      ) : (
                        <Badge className={STATUS_COLORS[booking.status] || STATUS_COLORS.pickup}>
                          {booking.status?.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-sm text-slate-500">
                      <span>{booking.vehicle_make} {booking.vehicle_model}</span>
                      {booking.customer_phone && (
                        <span className="ml-3">{formatAUMobile(booking.customer_phone)}</span>
                      )}
                    </div>

                    <p className="text-xs text-slate-400 mt-1">
                      {booking.booking_number}
                    </p>

                    {/* Quick action — Go to Job Card */}
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewJobCard(booking);
                        }}
                        data-testid={`booking-go-to-jc-${booking.booking_id}`}
                      >
                        <ClipboardList className="h-3.5 w-3.5 mr-1" />
                        Job Card
                      </Button>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="mr-6">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEditBooking(booking)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit / Reschedule
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onViewJobCard(booking)}>
                        <ClipboardList className="h-4 w-4 mr-2" />
                        View Job Card
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onSendSMS(booking)}>
                        <Send className="h-4 w-4 mr-2" />
                        Send SMS
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onStatusUpdate(booking.booking_id, 'in_progress')}>
                        <Wrench className="h-4 w-4 mr-2" />
                        Start Work
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onStatusUpdate(booking.booking_id, 'ready')}>
                        <Eye className="h-4 w-4 mr-2" />
                        Mark Ready
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onStatusUpdate(booking.booking_id, 'completed')}>
                        <Calendar className="h-4 w-4 mr-2" />
                        Complete
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {user && user.role === 'super_admin' && (
                        <DropdownMenuItem onClick={() => onDelete(booking)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// Table View Component (existing functionality)
function TableView({ 
  bookings, 
  loading, 
  onStatusUpdate, 
  onDelete, 
  onSendSMS,
  onViewJobCard,
  user,
  page,
  totalPages,
  onPageChange 
}) {
  return (
    <Card className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Customer</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Vehicle</TableHead>
            <TableHead>Service</TableHead>
            <TableHead>Status / Loan Car</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={6}>
                  <div className="h-10 bg-slate-100 animate-pulse rounded"></div>
                </TableCell>
              </TableRow>
            ))
          ) : bookings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-12">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No bookings found</p>
              </TableCell>
            </TableRow>
          ) : (
            bookings.map((booking) => (
              <TableRow key={booking.booking_id} data-testid={`booking-row-${booking.booking_id}`}>
                <TableCell>
                  {/* Customer Name - Clickable */}
                  <div 
                    className="font-medium text-slate-900 cursor-pointer hover:text-blue-600"
                    onClick={() => onViewJobCard(booking)}
                  >
                    {booking.customer_name || 'Unknown Customer'}
                  </div>
                  <p className="text-xs text-slate-400">{booking.booking_number}</p>
                </TableCell>
                <TableCell className="text-slate-600">
                  {formatMelbourne(booking.booked_datetime, {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                    year: undefined,
                  })}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Car className="h-4 w-4 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-900">{formatRego(booking.vehicle_rego) || '-'}</p>
                      <p className="text-xs text-slate-500">
                        {[booking.vehicle_make, booking.vehicle_model].filter(Boolean).join(' ')}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={SERVICE_TYPE_BG_COLORS[booking.service_type] || SERVICE_TYPE_BG_COLORS.other}>
                    {SERVICE_TYPE_LABELS[booking.service_type] || booking.service_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  {/* Show Loan Car badge if requested, otherwise status */}
                  {booking.loan_car_requested ? (
                    <Badge className="bg-cyan-100 text-cyan-700">
                      <Key className="h-3 w-3 mr-1" />
                      Loan Car
                    </Badge>
                  ) : (
                    <Badge className={STATUS_COLORS[booking.status] || STATUS_COLORS.pickup}>
                      {booking.status?.replace('_', ' ')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                      onClick={() => onViewJobCard(booking)}
                      data-testid={`row-go-to-jc-${booking.booking_id}`}
                    >
                      <ClipboardList className="h-3.5 w-3.5 mr-1" />
                      Job Card
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewJobCard(booking)}>
                          <ClipboardList className="h-4 w-4 mr-2" />
                          View Job Card
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onSendSMS(booking)}>
                          <Send className="h-4 w-4 mr-2" />
                          Send Confirmation SMS
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onStatusUpdate(booking.booking_id, 'in_progress')}>
                          <Wrench className="h-4 w-4 mr-2" />
                          Start Work
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onStatusUpdate(booking.booking_id, 'ready')}>
                          <Eye className="h-4 w-4 mr-2" />
                          Mark Ready
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onStatusUpdate(booking.booking_id, 'completed')}>
                          <Calendar className="h-4 w-4 mr-2" />
                          Complete
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user && user.role === 'super_admin' && (
                          <DropdownMenuItem onClick={() => onDelete(booking)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <p className="text-sm text-slate-600">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Bookings() {
  const { guardComms, commsDialog } = useCommsGate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // View mode: 'weekly' (default), 'monthly', 'yearly', 'list'.
  // When the URL carries `?search=X`, default straight to LIST so the SM
  // immediately sees the matching booking rather than the calendar.
  const [viewMode, setViewMode] = useState(
    searchParams.get('search') ? 'list' : 'weekly',
  );
  
  // Calendar data
  const [weekData, setWeekData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [yearData, setYearData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Day detail
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today's date
    return new Date().toISOString().split('T')[0];
  });
  const [dayBookings, setDayBookings] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [showQuickBooking, setShowQuickBooking] = useState(false);
  
  // Navigation state
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    return monday.toISOString().split('T')[0];
  });
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  
  // Table view state
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  // Search box — initialised from `?search=X` URL param (e.g. arriving
  // from the global search bar or another page). Empty by default.
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  // Related items (invoices / job cards / customers / vehicles matching
  // the same term). Fetched only when search has content.
  const [relatedItems, setRelatedItems] = useState(null);
  // Date filter for the list view — empty = all dates, YYYY-MM-DD =
  // narrow to that calendar day (Melbourne). Lets SM pull all no-shows
  // for a specific past day to call/reschedule customers.
  const [dateFilter, setDateFilter] = useState('');

  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  // When `?search=X` arrives in the URL (e.g. coming from the global
  // search bar or a deep link), auto-switch to the LIST view so the user
  // immediately sees the matching booking — not the calendar.
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch) {
      setViewMode('list');
      setSearchQuery(urlSearch);
    }
     
  }, [searchParams]);

  // Related items panel — fetch invoices / JCs / customers / vehicles
  // matching the current search term so the SM can pivot to any of them
  // while on the phone with the customer.
  useEffect(() => {
    const term = (searchQuery || '').trim();
    if (term.length < 2) { setRelatedItems(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/global-search?q=${encodeURIComponent(term)}&limit_each=8`,
          { credentials: 'include' },
        );
        if (r.ok) setRelatedItems(await r.json());
      } catch (_e) { /* swallow */ }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch week data
  const fetchWeekData = useCallback(async () => {
    if (!user) return;
    setCalendarLoading(true);
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/bookings/stats/weekly${siteParams}${sep}week_start=${currentWeekStart}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setWeekData(data);
      }
    } catch (error) {
      console.error('Failed to fetch week data:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [user, getSiteParams, currentWeekStart]);

  // Fetch month data
  const fetchMonthData = useCallback(async () => {
    if (!user) return;
    setCalendarLoading(true);
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/bookings/stats/monthly${siteParams}${sep}year=${currentYear}&month=${currentMonth}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMonthData(data);
      }
    } catch (error) {
      console.error('Failed to fetch month data:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [user, getSiteParams, currentYear, currentMonth]);

  // Fetch year data
  const fetchYearData = useCallback(async () => {
    if (!user) return;
    setCalendarLoading(true);
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      const url = `${BACKEND_URL}/api/bookings/stats/yearly${siteParams}${sep}year=${currentYear}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setYearData(data);
      }
    } catch (error) {
      console.error('Failed to fetch year data:', error);
    } finally {
      setCalendarLoading(false);
    }
  }, [user, getSiteParams, currentYear]);

  // Fetch bookings for table view
  const fetchBookings = useCallback(async (search = searchQuery) => {
    if (!user) return;
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      let url = `${BACKEND_URL}/api/bookings${siteParams}${sep}page=${page}&page_size=${pageSize}`;
      
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (serviceTypeFilter) url += `&service_type=${serviceTypeFilter}`;
      if (dateFilter) url += `&date=${dateFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setBookings(data.bookings);
        setTotalBookings(data.total);
        setTotalPages(data.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  }, [user, getSiteParams, page, pageSize, searchQuery, statusFilter, serviceTypeFilter, dateFilter]);

  // Fetch day bookings
  const fetchDayBookings = useCallback(async (date) => {
    if (!user) return;
    setDayLoading(true);
    try {
      const siteParams = getSiteParams();
      const url = `${BACKEND_URL}/api/bookings/stats/day/${date}${siteParams}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDayBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to fetch day bookings:', error);
    } finally {
      setDayLoading(false);
    }
  }, [user, getSiteParams]);

  // Load data based on view mode
  useEffect(() => {
    if (!user) return;
    
    if (viewMode === 'weekly') {
      fetchWeekData();
      // Also fetch today's bookings by default
      if (selectedDate) {
        fetchDayBookings(selectedDate);
      }
    } else if (viewMode === 'monthly') {
      fetchMonthData();
    } else if (viewMode === 'yearly') {
      fetchYearData();
    } else if (viewMode === 'list') {
      fetchBookings();
    }
  }, [user, viewMode, fetchWeekData, fetchMonthData, fetchYearData, fetchBookings]);

  // Handle day click
  const handleDayClick = (date) => {
    setSelectedDate(date);
    fetchDayBookings(date);
  };

  // Handle week click from monthly view
  const handleWeekClick = (weekStart) => {
    setCurrentWeekStart(weekStart);
    setViewMode('weekly');
  };

  // Handle month click from yearly view
  const handleMonthClick = (year, month) => {
    setCurrentYear(year);
    setCurrentMonth(month);
    setViewMode('monthly');
  };

  // Navigation handlers
  const navigateWeek = (direction) => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + (direction * 7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
    setSelectedDate(null);
  };

  const navigateMonth = (direction) => {
    let newMonth = currentMonth + direction;
    let newYear = currentYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const navigateYear = (direction) => {
    setCurrentYear(currentYear + direction);
  };

  // Action handlers
  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}${siteParams}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Booking status updated');
        // Refresh current view
        if (viewMode === 'weekly') {
          fetchWeekData();
          if (selectedDate) fetchDayBookings(selectedDate);
        } else if (viewMode === 'list') {
          fetchBookings();
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update booking:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (booking) => {
    if (!window.confirm(`Delete booking ${booking.booking_number}?`)) return;

    try {
      const siteParams = getSiteParams();
      const response = await fetch(`${BACKEND_URL}/api/bookings/${booking.booking_id}${siteParams}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Booking deleted');
        // Refresh current view
        if (viewMode === 'weekly') {
          fetchWeekData();
          if (selectedDate) fetchDayBookings(selectedDate);
        } else if (viewMode === 'list') {
          fetchBookings();
        }
      } else {
        toast.error('Failed to delete booking');
      }
    } catch (error) {
      console.error('Failed to delete booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const sendConfirmationSMS = async (booking) => {
    if (!booking.customer_phone) {
      toast.error('No phone number available for this booking');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/sms/resend/${booking.booking_id}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.result?.status === 'mock_sent' || data.result?.status === 'sent') {
          toast.success(`Confirmation SMS sent for ${booking.booking_number}`);
        } else if (data.result?.status === 'skipped') {
          toast.warning(`SMS skipped: ${data.result?.reason?.replace('_', ' ')}`);
        } else {
          toast.error(`SMS failed: ${data.result?.error || 'Unknown error'}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Failed to send confirmation SMS:', error);
      toast.error('Failed to send confirmation SMS');
    }
  };

  // Handle opening edit modal
  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    setEditModalOpen(true);
  };

  // Handle booking update from modal
  const handleBookingUpdate = () => {
    // Refresh the current view
    if (viewMode === 'weekly') {
      fetchWeekData();
      if (selectedDate) fetchDayBookings(selectedDate);
    } else if (viewMode === 'monthly') {
      fetchMonthData();
    } else if (viewMode === 'yearly') {
      fetchYearData();
    } else if (viewMode === 'list') {
      fetchBookings();
    }
  };

  // Handle view job card - navigate directly to the linked JC if possible.
  const handleViewJobCard = async (booking) => {
    // 1) If the booking already has a job_card_id, deep-link straight to it.
    if (booking.job_card_id) {
      navigate(`/job-cards?id=${booking.job_card_id}`);
      return;
    }
    try {
      const siteParams = getSiteParams();
      const sep = siteParams ? '&' : '?';
      const response = await fetch(
        `${BACKEND_URL}/api/job-cards${siteParams}${sep}booking_id=${booking.booking_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const list = data.job_cards || data.items || [];
        if (list.length > 0 && list[0].job_card_id) {
          navigate(`/job-cards?id=${list[0].job_card_id}`);
          return;
        }
      }
      // Fallback: search by rego on the kanban view.
      if (booking.vehicle_rego) {
        navigate(`/job-cards?search=${encodeURIComponent(booking.vehicle_rego)}`);
      } else {
        toast.info('No job card found for this booking yet');
        navigate('/job-cards');
      }
    } catch (error) {
      console.error('Failed to view job card:', error);
      navigate(`/job-cards${booking.vehicle_rego ? `?search=${encodeURIComponent(booking.vehicle_rego)}` : ''}`);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBookings(searchQuery);
  };

  // Get title based on view mode
  const getViewTitle = () => {
    if (viewMode === 'weekly' && weekData) {
      return weekData.title;
    } else if (viewMode === 'monthly' && monthData) {
      return monthData.title;
    } else if (viewMode === 'yearly') {
      return currentYear.toString();
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="bookings-container">
        {/* Booking Edit Modal */}
        <BookingEditModal
          booking={editingBooking}
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingBooking(null);
          }}
          onUpdate={handleBookingUpdate}
          onCancel={handleBookingUpdate}
          getSiteParams={getSiteParams}
        />

        {/* Quick Booking popup (phone-call workflow) */}
        <QuickBookingModal
          open={showQuickBooking}
          onClose={() => setShowQuickBooking(false)}
          onCreated={() => {
            // Refresh whichever view is currently active so the new
            // booking shows up immediately (no manual page refresh).
            fetchBookings();
            if (viewMode === 'weekly') {
              fetchWeekData();
              if (selectedDate) fetchDayBookings(selectedDate);
            } else if (viewMode === 'monthly') {
              fetchMonthData();
            } else if (viewMode === 'yearly') {
              fetchYearData();
            }
          }}
          getSiteParams={getSiteParams}
        />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="font-['Chivo'] font-bold text-4xl sm:text-5xl text-slate-900 mb-2">
              Bookings
            </h1>
            <p className="text-slate-500 text-lg">
              {viewMode === 'list' ? `${totalBookings} total booking${totalBookings !== 1 ? 's' : ''}` : getViewTitle()}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setShowQuickBooking(true)} 
              className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700"
              data-testid="new-booking-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Booking
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/kiosk')} 
              className="h-12 px-6"
              data-testid="kiosk-check-in-button"
              title="Open kiosk check-in (full screen)"
            >
              Kiosk
            </Button>
          </div>
        </div>

        {/* View Toggle and Navigation */}
        <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* View Mode Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-600 mr-2">View:</span>
              <div className="flex bg-slate-100 rounded-lg p-1">
                <Button
                  variant={viewMode === 'weekly' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('weekly')}
                  className={`h-8 px-3 ${viewMode === 'weekly' ? 'bg-white shadow-sm' : ''}`}
                  data-testid="view-weekly-btn"
                >
                  <CalendarDays className="h-4 w-4 mr-1.5" />
                  Weekly
                </Button>
                <Button
                  variant={viewMode === 'monthly' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('monthly')}
                  className={`h-8 px-3 ${viewMode === 'monthly' ? 'bg-white shadow-sm' : ''}`}
                  data-testid="view-monthly-btn"
                >
                  <CalendarRange className="h-4 w-4 mr-1.5" />
                  Monthly
                </Button>
                <Button
                  variant={viewMode === 'yearly' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('yearly')}
                  className={`h-8 px-3 ${viewMode === 'yearly' ? 'bg-white shadow-sm' : ''}`}
                  data-testid="view-yearly-btn"
                >
                  <LayoutGrid className="h-4 w-4 mr-1.5" />
                  Yearly
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`h-8 px-3 ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                  data-testid="view-list-btn"
                >
                  <List className="h-4 w-4 mr-1.5" />
                  List
                </Button>
              </div>
            </div>

            {/* Navigation Controls (for calendar views) */}
            {viewMode !== 'list' && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (viewMode === 'weekly') navigateWeek(-1);
                    else if (viewMode === 'monthly') navigateMonth(-1);
                    else navigateYear(-1);
                  }}
                  data-testid="nav-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-[180px] text-center">
                  <span className="font-medium text-slate-700">{getViewTitle()}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (viewMode === 'weekly') navigateWeek(1);
                    else if (viewMode === 'monthly') navigateMonth(1);
                    else navigateYear(1);
                  }}
                  data-testid="nav-next-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const now = new Date();
                    if (viewMode === 'weekly') {
                      const dayOfWeek = now.getDay();
                      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                      const monday = new Date(now);
                      monday.setDate(now.getDate() + diff);
                      setCurrentWeekStart(monday.toISOString().split('T')[0]);
                    } else if (viewMode === 'monthly') {
                      setCurrentMonth(now.getMonth() + 1);
                      setCurrentYear(now.getFullYear());
                    } else {
                      setCurrentYear(now.getFullYear());
                    }
                    setSelectedDate(null);
                  }}
                  data-testid="nav-today-btn"
                >
                  Today
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  if (viewMode === 'weekly') fetchWeekData();
                  else if (viewMode === 'monthly') fetchMonthData();
                  else fetchYearData();
                }}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* List View Filters */}
            {viewMode === 'list' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <form onSubmit={handleSearch} className="flex gap-2 flex-1">
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 w-48"
                    data-testid="search-input"
                  />
                  <Button type="submit" variant="outline" size="sm">
                    <Search className="h-4 w-4" />
                  </Button>
                </form>
                
                <Select value={statusFilter || 'all'} onValueChange={(val) => { setStatusFilter(val === 'all' ? '' : val); setPage(1); }}>
                  <SelectTrigger className="w-36 h-9" data-testid="status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="booking">Booking (awaiting customer)</SelectItem>
                    <SelectItem value="no_show">⚠ No Show</SelectItem>
                    <SelectItem value="pickup">Dropped-off</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="awaiting_parts">Awaiting Parts</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date filter — narrow list to a specific calendar day
                    (e.g. "show me all of yesterday's no-shows"). */}
                <div className="flex items-center gap-1 bg-white border rounded-md px-2 h-9">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
                    className="h-7 w-36 border-0 p-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                    data-testid="bookings-date-filter"
                    aria-label="Filter by booked date"
                  />
                  {dateFilter && (
                    <button
                      type="button"
                      onClick={() => { setDateFilter(''); setPage(1); }}
                      className="text-slate-400 hover:text-slate-600 text-sm"
                      data-testid="bookings-date-filter-clear"
                      aria-label="Clear date filter"
                    >
                      ×
                    </button>
                  )}
                </div>

                <Select value={serviceTypeFilter} onValueChange={(val) => { setServiceTypeFilter(val === 'all' ? '' : val); setPage(1); }}>
                  <SelectTrigger className="w-32 h-9" data-testid="service-type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="rwc">RWC</SelectItem>
                    <SelectItem value="tyres">Tyres</SelectItem>
                    <SelectItem value="aircon">Aircon</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm" onClick={() => fetchBookings()}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Calendar Views */}
        {viewMode === 'weekly' && (
          <>
            <WeeklyCalendarView
              weekData={weekData}
              onDayClick={handleDayClick}
              selectedDate={selectedDate}
              loading={calendarLoading}
            />
            <DayDetailPanel
              date={selectedDate}
              bookings={dayBookings}
              loading={dayLoading}
              onClose={() => setSelectedDate(null)}
              onStatusUpdate={handleUpdateStatus}
              onDelete={handleDelete}
              onSendSMS={guardComms(sendConfirmationSMS)}
              onEditBooking={handleEditBooking}
              onViewJobCard={handleViewJobCard}
              user={user}
            />
          </>
        )}

        {viewMode === 'monthly' && (
          <MonthlyCalendarView
            monthData={monthData}
            onWeekClick={handleWeekClick}
            loading={calendarLoading}
          />
        )}

        {viewMode === 'yearly' && (
          <YearlyCalendarView
            yearData={yearData}
            onMonthClick={handleMonthClick}
            loading={calendarLoading}
          />
        )}

        {/* Table View */}
        {viewMode === 'list' && (
          <TableView
            bookings={bookings}
            loading={false}
            onStatusUpdate={handleUpdateStatus}
            onDelete={handleDelete}
            onSendSMS={guardComms(sendConfirmationSMS)}
            onViewJobCard={handleViewJobCard}
            user={user}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}

        {/* Related-items panel — visible the moment SM types in the
            search box. Surfaces invoices / job cards / customers / vehicles
            tied to the same phone number / rego / name so the SM can pull
            full context on the phone. */}
        <RelatedItemsPanel
          results={relatedItems}
          currentTerm={searchQuery}
          primaryType="bookings"
        />

        {/* Legend */}
        {viewMode !== 'list' && (
          <Card className="mt-6 p-4 bg-slate-50">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-600">Service Types:</span>
              {Object.entries(SERVICE_TYPE_LABELS).map(([type, label]) => (
                <div key={type} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded-full ${SERVICE_TYPE_COLORS[type]}`}></div>
                  <span className="text-sm text-slate-600">{label}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      {commsDialog}
    </Layout>
  );
}
